// OutboundEditRuleModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { cleanString } from '../../../apiUtils';
import { normalizeRuleEvent } from '../../utils/integrationUtils';

// Constants
import { RULE_EVENT_OPTIONS } from '../../constants/integrationOptions';

// Components
import ModalActions from './ModalActions';
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * OutboundEditRuleModal
 *
 * Renders the modal used to edit an existing outbound integration rule.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Handler to close the modal.
 * @param {boolean} canManageCredentials - Whether the user can manage outbound rules.
 * @param {string} busyActionKey - Current busy action key used to disable actions.
 * @param {object} editingRule - Rule currently being edited.
 * @param {string} editDraftName - Draft rule name.
 * @param {string} editDraftEvent - Draft trigger event.
 * @param {string} editDraftTypeId - Draft type id.
 * @param {string} editDraftStatus - Draft rule status.
 * @param {string} editDraftDestinationUrl - Draft destination url.
 * @param {string} editDraftDestinationName - Draft destination name.
 * @param {array} typeRows - Available type options.
 * @param {function} onEditDraftNameChange - Handler to update draft name.
 * @param {function} onEditDraftEventChange - Handler to update draft event.
 * @param {function} onEditDraftTypeIdChange - Handler to update draft type id.
 * @param {function} onEditDraftStatusChange - Handler to update draft status.
 * @param {function} onEditDraftDestinationUrlChange - Handler to update draft destination url.
 * @param {function} onEditDraftDestinationNameChange - Handler to update draft destination name.
 * @param {function} onSave - Handler to save rule edits.
 *
 * @returns {ReactNode} The rendered edit outbound rule modal.
 */
const OutboundEditRuleModal = ({
    theme,
    isOpen,
    onClose,
    canManageCredentials,
    busyActionKey,
    editingRule,
    editDraftName,
    editDraftEvent,
    editDraftTypeId,
    editDraftStatus,
    editDraftDestinationUrl,
    editDraftDestinationName,
    typeRows,
    onEditDraftNameChange,
    onEditDraftEventChange,
    onEditDraftTypeIdChange,
    onEditDraftStatusChange,
    onEditDraftDestinationUrlChange,
    onEditDraftDestinationNameChange,
    onSave,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    const editingRuleId = cleanString(editingRule?.id || editingRule?.key);

    // ----- RENDER ----- //

    return (
        <Modal
          title="Edit Outbound Rule"
          isOpen={isOpen}
          width={isMobile ? '100%' : 620}
          maxHeight="85vh"
          onClose={onClose}
        >
            <div className="api-integrations-modal-body">
                <div
                  className="api-integrations-subtitle"
                  style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Update rule settings and destination details.
                </div>

                <div className="api-integrations-form-grid">
                    <div
                      className="api-integrations-field-label"
                      style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Rule name
                    </div>
                    <TextInput
                      value={editDraftName}
                      placeholder="Example: Tasks -> Zapier Sheet"
                      disabled={!canManageCredentials}
                      onChange={(event) => onEditDraftNameChange(event.target.value)}
                    />

                    <div
                      className="api-integrations-field-label"
                      style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Trigger event
                    </div>
                    <SelectInput
                      value={editDraftEvent}
                      disabled={!canManageCredentials}
                      options={RULE_EVENT_OPTIONS}
                      onChange={(event) => onEditDraftEventChange(normalizeRuleEvent(event.target.value))}
                    />

                    <div
                      className="api-integrations-field-label"
                      style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Trigger type
                    </div>
                    <SelectInput
                      value={editDraftTypeId}
                      disabled={!canManageCredentials || typeRows.length === 0}
                      options={typeRows.map((row) => ({
                        value: row.typeId,
                        label: row.label,
                      }))}
                      onChange={(event) => onEditDraftTypeIdChange(cleanString(event.target.value))}
                    />

                    <div
                      className="api-integrations-field-label"
                      style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Rule status
                    </div>
                    <SelectInput
                      value={editDraftStatus}
                      disabled={!canManageCredentials}
                      options={[
                        { value: 'active', label: 'Active' },
                        { value: 'paused', label: 'Paused' },
                      ]}
                      onChange={(event) => onEditDraftStatusChange(cleanString(event.target.value).toLowerCase() === 'paused' ? 'paused' : 'active')}
                    />

                    <div
                      className="api-integrations-field-label"
                      style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Destination URL
                    </div>
                    <TextInput
                      value={editDraftDestinationUrl}
                      placeholder="https://hooks.zapier.com/hooks/catch/..."
                      disabled={!canManageCredentials}
                      onChange={(event) => onEditDraftDestinationUrlChange(event.target.value)}
                    />

                    <div
                      className="api-integrations-field-label"
                      style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Destination name (optional)
                    </div>
                    <TextInput
                      value={editDraftDestinationName}
                      placeholder="Zapier Catch Hook"
                      disabled={!canManageCredentials}
                      onChange={(event) => onEditDraftDestinationNameChange(event.target.value)}
                    />
                </div>

                <ModalActions>
                    <Button
                      label="Cancel"
                      enabled={busyActionKey !== `edit:${editingRuleId}`}
                      height="30px"
                      fontSize="12px"
                      borderRadius="6px"
                      onClick={onClose}
                    />
                    <Button
                      label="Save Changes"
                      enabled={canManageCredentials && busyActionKey !== `edit:${editingRuleId}`}
                      height="30px"
                      fontSize="12px"
                      borderRadius="6px"
                      onClick={onSave}
                    />
                </ModalActions>
            </div>
        </Modal>
    );
};

// Dev Tools
OutboundEditRuleModal.displayName = 'OutboundEditRuleModal';

export default OutboundEditRuleModal;
